import React, { useEffect, useState } from "react";
import { programTypeToName } from "./constants/programTypes";
import * as firebase from "firebase";
import Push from "push.js";
import difference from "lodash.difference";
import { Alert, Button, Icon, Spin, Switch } from "antd";
import { v4 as uuidv4 } from "uuid";
import {
  IoCheckmarkCircle,
  IoCloseCircle,
  IoInformationCircle,
  IoInformationCircleOutline,
  IoWarning,
} from "react-icons/io5";
import SpeedBadge from "./SpeedBadge";
import { getNameForLocationId, REMOTE_LOCATION_ID } from "./utils/locationUtils";
import moment from "moment";
import { handleSubmitRef } from "./utils/preferenceUtils";
import BrowserAlertsTipsModal from "./BrowserAlertsTipsModal";
import Bowser from "bowser";
import {
  getDayNameFromNumber,
  getNonExcludedDayNumbersFromExcludedDays,
} from "./utils/dateUtils";

const browser = Bowser.getParser(window.navigator.userAgent);

const isDesktopBrowser = browser.getPlatformType() === "desktop";

// When true, fetch slots directly from TTP API instead of Firestore.
// NOTE: Must match USE_FRONTEND_FETCH_FOR_BROWSER_ALERTS in functions/services/ttpApiService.js
const USE_FRONTEND_FETCH_FOR_BROWSER_ALERTS = true;

// Polling interval for direct TTP API fetch (10 seconds to match backend)
const TTP_POLL_INTERVAL_MS = 10 * 1000;

const TTP_API_BASE = "https://ttp.cbp.dhs.gov/schedulerapi/slots";

const getTimeStampFromSlotId = (slotId) =>
  slotId.substring(slotId.indexOf("-") + 1, slotId.length);

const localSlotsSeen = {};

const getValidDateSlotIds = (slotIds, currentInterviewDate, excludedDays) => {
  return slotIds.filter((slotId) => {
    const isSlotBeforeCutoffDate = moment(
      getTimeStampFromSlotId(slotId)
    ).isBefore(currentInterviewDate, "day");

    const startTimestamp = getTimeStampFromSlotId(slotId);

    const dayNumber = moment(startTimestamp).day();
    const isSlotOnExcludedDay = excludedDays.includes(dayNumber);
    return isSlotBeforeCutoffDate && !isSlotOnExcludedDay;
  });
};

const fetchSlotsFromTtpApi = async (locationId) => {
  const isRemote = String(locationId) === String(REMOTE_LOCATION_ID);
  const url = isRemote
    ? `${TTP_API_BASE}?orderBy=soonest&limit=200&minimum=1&remote=true`
    : `${TTP_API_BASE}?orderBy=soonest&limit=200&locationId=${locationId}&minimum=1`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`TTP API error: ${response.status}`);
  }
  const slots = await response.json();
  return slots.map((slot) => `${slot.locationId}-${slot.startTimestamp}`);
};

const BrowserAlertsSection = ({ userInfo, locationMap, fetchUserInfo }) => {
  const db = firebase.firestore();

  const nonExcludedDays =
    userInfo.excludedDays?.length &&
    getNonExcludedDayNumbersFromExcludedDays(userInfo.excludedDays);

  const enrollmentCenters = userInfo.enrollmentCenters;
  const isEnabled = userInfo.isEnabled;

  const [currentRenderId, setCurrentRenderId] = useState(null);
  const [showLoadingIndicators, setShowLoadingIndicators] = useState(false);
  const [isBrowserAlertsEnabledLocally, setIsBrowserAlertsEnabledLocally] =
    useState(userInfo.isBrowserAlertsEnabled);
  const isBrowserAlertsEnabled = userInfo.isBrowserAlertsEnabled;
  const [showTipsModal, setShowTipsModal] = useState(false);

  const hasNotificationPermissions = Push.Permission.has();

  useEffect(() => {
    setIsBrowserAlertsEnabledLocally(isBrowserAlertsEnabled);
  }, [isBrowserAlertsEnabled]);

  const reRender = () => {
    setCurrentRenderId(uuidv4());
  };

  useEffect(() => {
    if (!hasNotificationPermissions && isBrowserAlertsEnabledLocally) {
      Push.Permission.request(
        () => {
          reRender();
        },
        () => {
          reRender();
        }
      );
    }
  }, [hasNotificationPermissions, isBrowserAlertsEnabledLocally]);

  const processNewSlotsForLocation = (locationId, newSlotIds) => {
    const validDateNewSlotIds = getValidDateSlotIds(
      newSlotIds,
      userInfo.currentInterviewDate,
      userInfo.excludedDays
    );

    const newlyAddedSlots = difference(
      validDateNewSlotIds,
      localSlotsSeen[locationId]
    );

    console.log("newSlots", newSlotIds);
    console.log("localSlotsSeen", localSlotsSeen);
    console.log("newlyAddedSlots", newlyAddedSlots);

    const locationName = getNameForLocationId(locationId, locationMap);
    const isFirstRun = !localSlotsSeen[locationId];

    if (isFirstRun) {
      localSlotsSeen[locationId] = [];
    }

    const areThereNewSlots = newlyAddedSlots?.length;

    if (areThereNewSlots && !isFirstRun && isEnabled) {
      const newlyAddedSlotTimes = newlyAddedSlots.map((slotId) =>
        moment(getTimeStampFromSlotId(slotId)).format("LLLL")
      );

      if (isBrowserAlertsEnabledLocally) {
        Push.create(
          `${newlyAddedSlotTimes.length} New ${
            programTypeToName[userInfo.program]
          } appointment` + (newlyAddedSlotTimes.length > 1 ? "s" : ""),
          {
            body:
              (locationName ? locationName : "") + newlyAddedSlotTimes,
            icon: "/icon.png",
            requireInteraction: true,
            silent: false,
            vibrate: true,
            onClick: function () {
              window.focus();
              this.close();
              window.open("https://ttp.dhs.gov", "_blank");
            },
          }
        );
      }
    }
    localSlotsSeen[locationId] = validDateNewSlotIds;
    reRender();
    console.log("after", localSlotsSeen);
  };

  // Direct TTP API polling (no Firestore reads)
  // Uses Web Locks API to ensure only one tab polls and sends notifications.
  // Non-leader tabs queue silently; if the leader closes, the next tab takes over.
  useEffect(() => {
    if (!USE_FRONTEND_FETCH_FOR_BROWSER_ALERTS) return;
    if (!isEnabled) return;
    if (!isBrowserAlertsEnabledLocally) return;

    let cancelled = false;

    const pollAllLocations = async () => {
      await Promise.all(
        enrollmentCenters.map(async (locationId) => {
          try {
            const slotIds = await fetchSlotsFromTtpApi(locationId);
            if (!cancelled) {
              processNewSlotsForLocation(locationId, slotIds);
            }
          } catch (err) {
            console.error(`Error fetching slots for location ${locationId}:`, err);
          }
        })
      );
    };

    const runPollingLoop = async () => {
      await pollAllLocations();
      while (!cancelled) {
        await new Promise((resolve) => setTimeout(resolve, TTP_POLL_INTERVAL_MS));
        if (!cancelled) {
          await pollAllLocations();
        }
      }
    };

    if (navigator.locks) {
      console.log("[BrowserAlerts] Requesting lock for TTP polling...");
      navigator.locks.request("ges-browser-alerts-polling", async () => {
        console.log("[BrowserAlerts] Lock acquired — this tab is the polling leader");
        if (!cancelled) {
          await runPollingLoop();
        }
        console.log("[BrowserAlerts] Lock released — polling stopped");
      });
    } else {
      console.log("[BrowserAlerts] Web Locks not available, polling without lock");
      runPollingLoop();
    }

    return () => {
      console.log("[BrowserAlerts] Cleanup — releasing lock");
      cancelled = true;
    };
  }, [isBrowserAlertsEnabledLocally]);

  // Firestore onSnapshot listener (original path)
  // Uses Web Locks API to ensure only one tab subscribes and sends notifications.
  useEffect(() => {
    if (USE_FRONTEND_FETCH_FOR_BROWSER_ALERTS) return;
    if (!isEnabled) return;

    let cancelled = false;
    let unsubscribeHandlers = [];

    const startListening = () => {
      var liveAppointmentsRef = db.collection("live-appointments");

      unsubscribeHandlers = enrollmentCenters.map((locationId) => {
        return liveAppointmentsRef.doc(locationId).onSnapshot(
          { includeMetadataChanges: true },
          (doc) => {
            const locationData = doc.data();
            const newSlots = locationData?.slots || [];
            processNewSlotsForLocation(locationId, newSlots);
          }
        );
      });

      // Hold the lock until cleanup
      return new Promise((resolve) => {
        const checkCancelled = setInterval(() => {
          if (cancelled) {
            clearInterval(checkCancelled);
            resolve();
          }
        }, 1000);
      });
    };

    if (navigator.locks) {
      console.log("[BrowserAlerts] Requesting lock for Firestore listeners...");
      navigator.locks.request("ges-browser-alerts-polling", async () => {
        console.log("[BrowserAlerts] Lock acquired — this tab is the Firestore listener leader");
        if (!cancelled) {
          await startListening();
        }
        console.log("[BrowserAlerts] Lock released — Firestore listeners stopped");
      });
    } else {
      console.log("[BrowserAlerts] Web Locks not available, listening without lock");
      startListening();
    }

    return () => {
      cancelled = true;
      unsubscribeHandlers.forEach((handler) => handler());
    };
  }, [isBrowserAlertsEnabledLocally]);
  return (
    <>
      <div style={{ maxWidth: "600px", textAlign: "left", marginTop: "50px" }}>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ flexShrink: 0 }}>Live appointment openings</h2>
        </div>
        {isEnabled && (
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            direction: "column",
            padding: "20px 0",
          }}
        >
          <div>
            <Spin
              indicator={
                <Icon
                  type="loading"
                  style={{ fontSize: 20, color: "#32de84", marginRight: "5px" }}
                  spin
                />
              }
            />
            Scanning for appointments before{" "}
            <a href="#cutoff">
              {moment(userInfo.currentInterviewDate).format("LL")}
            </a>
            {nonExcludedDays.length < 7 ? (
              <>
                {" "}
                on{" "}
                <a href="#cutoff">
                  {nonExcludedDays.map(
                    (dayNumber, i) =>
                      getDayNameFromNumber(dayNumber) +
                      (i < nonExcludedDays.length - 1 ? ", " : "")
                  )}
                </a>
              </>
            ) : (
              <>
                {" "}
                on <a href="#cutoff">any day of the week</a>
              </>
            )}
          </div>
          <Button size="small" type="ghost" href="#cutoff">
            Edit
          </Button>
        </div>
        )}
        <div style={{ display: "flex", marginTop: "40px" }}>
          <Switch
            disabled={!isDesktopBrowser}
            checked={isBrowserAlertsEnabledLocally}
            onChange={(checked) => {
              setIsBrowserAlertsEnabledLocally(checked);
              if (handleSubmitRef.current) {
                handleSubmitRef.current(null, {
                  isBrowserAlertsEnabled: checked,
                });
              }
            }}
          />
          <h3
            style={{
              marginLeft: "10px", //backgroundColor: "#32de84"
            }}
          >
            Browser alerts (unlimited)
          </h3>

          <SpeedBadge
            numBolts={3}
            style={{
              marginLeft: "10px", //backgroundColor: "#32de84"
            }}
          >
            Fastest
          </SpeedBadge>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", maxWidth: "600px" }}>
        {/* <IoInformationCircle color="lightblue" size="30px" /> */}
        <p>
          Keep this dashboard open while you're at your computer to get
          unlimited browser alerts on new openings. While you're away, text
          message and email alerts have you covered.
        </p>
      </div>
      {!isDesktopBrowser && (
        <Alert
          style={{ marginTop: "10px", width: "100%", marginBottom: "10px" }}
          type="warning"
          message="Open this page on a computer to use browser alerts"
          description="Browser notifications are not supported on phones or tablets"
        />
      )}

      {!hasNotificationPermissions &&
        isBrowserAlertsEnabledLocally &&
        isDesktopBrowser && (
          <Alert
            style={{
              marginTop: "10px",
              width: "100%",
              marginBottom: "10px",
            }}
            type="warning"
            message={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  {" "}
                  <IoWarning size="20" color="black" />
                  <p style={{ margin: 0, padding: 0 }}>
                    <strong>
                      Grant permission to send browser notifications{" "}
                    </strong>
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setShowTipsModal(true);
                  }}
                >
                  Show me how
                </Button>
              </div>
            }
            // description={<></>}
          />
        )}

      {hasNotificationPermissions && isDesktopBrowser && (
        <Button
          size="xs"
          onClick={() => {
            Push.create(
              `EXAMPLE: New ${programTypeToName[userInfo.program]} appointment`,
              {
                body: `Example Airport at Example Time on Example Date. Click to visit ${
                  programTypeToName[userInfo.program]
                } site.`,
                icon: "/icon.png",
                timeout: 4000,
                onClick: function () {
                  window.focus();
                  this.close();
                  window.open("https://ttp.dhs.gov", "_blank");
                },
              }
            );
          }}
        >
          Show me an example browser alert
        </Button>
      )}

      <div style={{ maxWidth: "600px", textAlign: "left", marginTop: "20px" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            direction: "column",
          }}
        >
          <strong>
            <p>Center</p>
          </strong>
          <strong>
            <p>Current matching openings</p>
          </strong>
        </div>
        {enrollmentCenters.map((locationId) => (
          <>
            <div
              style={{
                padding: "10px",
                boxShadow: "2px 2px 10px rgba(0,0,0,0.1)",
                borderRadius: "6px",
                display: "flex",
                justifyContent: "space-between",
                // height: "100px",
                alignItems: "center",
                border: "1px lightgray solid",
                marginBottom: "10px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {isEnabled && !localSlotsSeen[locationId] ? (
                  <Icon
                    type="loading"
                    style={{ fontSize: 24, color: "blue", marginRight: "5px" }}
                    spin
                  />
                ) : null}

                {!isEnabled || (localSlotsSeen[locationId] &&
                !localSlotsSeen[locationId].length) ? (
                  <IoCloseCircle color="red" size="25px" />
                ) : null}

                {localSlotsSeen[locationId]?.length ? (
                  <IoCheckmarkCircle color="#32de84" size="25px" />
                ) : null}
                <div style={{ marginLeft: "7px" }}>
                  {getNameForLocationId(locationId, locationMap)}
                </div>
              </div>
              <div>
                {localSlotsSeen[locationId]
                  ? localSlotsSeen[locationId].length
                  : null}
              </div>
            </div>
          </>
        ))}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div>
            <Button
              size="small"
              type="ghost"
              href="#centers"
              style={{ marginTop: "10px" }}
            >
              Edit centers
            </Button>
          </div>
          <div>
            <Button
              href="https://ttp.dhs.gov"
              target="_blank"
              style={{ marginTop: "20px" }}
            >
              Visit {programTypeToName[userInfo.program]} Site
            </Button>
          </div>
        </div>
      </div>

      {showTipsModal && (
        <BrowserAlertsTipsModal
          onClose={() => {
            setShowTipsModal(false);
          }}
        />
      )}
    </>
  );
};

export default BrowserAlertsSection;
